import { UserIcon } from '@heroicons/react/20/solid'
import React from 'react'

import useMsgs from '@/gf/hooks/useMsgs'
import useUppy from '@/gf/hooks/useUppy'

import useToggle from '@/gf/hooks/useToggle'
import useSession from '../hooks/useSession'

import { useUpdateProfileImageMutation } from '@/buyers/_gen/gql'
import Action from '@/gf/components/Action'
import ModalForm from '@/gf/components/ModalForm'
import UppyFileInput from '@/gf/components/UppyFileInput'
import { PhotoIcon } from '@heroicons/react/24/outline'
import classNames from 'classnames'
import useReloadSession from '../hooks/useReloadSession'

interface File {
  id: string
  name: string
  extension: string
  type?: string
  data: string
}

const sizes = { md: '20', lg: '32' }
type Size = 'md' | 'lg'

const allowedFileTypes = ['image/*', '.jpg', '.jpeg', '.png']

const imageBaseClassName = (size: Size) =>
  `h-${sizes[size]} w-${sizes[size]} rounded-xl object-cover shadow`

const ChangeProfileImage = ({
  size = 'md',
  showEditAlways = false,
  className,
}: {
  size?: Size
  showEditAlways?: boolean
  className?: string
}) => {
  const [_, msgr] = useMsgs()
  const { user } = useSession()
  const reloadSession = useReloadSession()
  const [spinnerLive, spinnerToggle] = useToggle()
  const [uploadFile, setUploadFile] = React.useState<File>()
  const [updating, setUpdating] = React.useState(false)
  const [update] = useUpdateProfileImageMutation()

  const uppy = useUppy({
    autoProceed: false,
    uploadContext: 'dealer_profile_image',
    onFilesAdded: ([file]) => {
      const reader = new FileReader()
      reader.onloadend = (e) => {
        if (e.target?.result && typeof e.target.result === 'string') {
          const fileWithData: File = {
            id: file.id,
            name: file.name,
            extension: file.extension,
            type: file.type,
            data: e.target.result,
          }
          setUploadFile(fileWithData)
        }
      }
      reader.readAsDataURL(file.data)
    },
    allowedFileTypes,
    onComplete: ([file]) => {
      update({ variables: { url: file.url } }).then(() => {
        reloadSession()
        msgr.add('Profile image updated.', 'positive')
        setUpdating(false)
      })
    },
    maxNumberOfFiles: 1,
  })

  return (
    <>
      <ModalForm
        title="Update Profile Image"
        open={updating}
        onClose={() => setUpdating(false)}
        onSubmit={(e) => {
          e.preventDefault()
          spinnerToggle.on()
          uppy.upload()
        }}
        submitButtonShowSpinner={spinnerLive}
      >
        <div className="mt-2 flex flex-col space-y-4">
          <UppyFileInput
            uppy={uppy}
            onFileInputError={(error) => msgr.add(error, 'negative')}
            allowedFileTypes={allowedFileTypes}
          />
          {uploadFile && (
            <div className="flex flex-col space-y-2 items-center">
              <p className="text-base text-gray-700">Preview</p>
              <img
                className={imageBaseClassName(size)}
                src={uploadFile.data}
                alt={uploadFile.name}
              />
            </div>
          )}
        </div>
      </ModalForm>
      <div className={classNames('flex gap-x-4 group', className)}>
        {user.profileImageUrl ? (
          <img src={user.profileImageUrl} alt="Profile" className={imageBaseClassName(size)} />
        ) : (
          <UserIcon className={`h-${sizes[size]} w-${sizes[size]} text-slate-400`} />
        )}

        <Action.S
          type="button"
          className={`self-start ${!showEditAlways && user.profileImageUrl && `hidden`}`}
          onClick={() => setUpdating(true)}
        >
          <PhotoIcon className="w-5 h-5 text-gearflow hover:text-orange-700 inline-block" /> Change
          Picture
        </Action.S>
      </div>
    </>
  )
}

export default ChangeProfileImage
